import React, {useEffect, useMemo} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useImmer} from 'use-immer';
import {SelectBoxComponent, TileListComponent} from '@/components';
import {cn} from '@/utils';
import Format from './Format';
import Paper from './Paper';
import RollDirection from './RollDirection';
import BookBinding from './BookBinding';
import DataCheck from "./DataCheck";
import Refinement from './Refinement';
import RollLabelOptions from "./RollLabelOptions";
import ShapeIcon from "./ShapeIcon";
import ModelIcon from "./ModelIcon";
import Finishing from "./Finishing";
import Page from "./Page";
import {matrixSelector, pageOptionsSelector, fetchChangeOptions, resetDpoData} from '../../slices/matrixSlice';
import {appSelector, dpleEnabledSelector} from "../../slices/appSlice";

// Options locked by the predesign in DPLE mode. The server applies the same
// filter via `pageOptionsHide` on subsequent fetches; this guards the initial
// SSR'd render where pageOptions were computed without DPLE awareness.
const DPLE_HIDDEN_OPTIONS = new Set(['shape', 'format']);

const Parts = ({instances}) => {
  const {
    isFetching,
    pageOptions,
    config: {overwrite_icons, custom_format, show_roll_label_options, is_custom_shape},
    customShape: {dpoData},
    productId,
    defaultConfig,
    customQuantities,
    optionsOverwrite,
    previewIconsPath,
    denyCustomFormats,
    lastChanges,
    pageOptionsHide,
    pageOptionsFilter,
    priceListStyle,
    AveryCustom: { client, customVat }
  } = useSelector(matrixSelector);
    const {
      application,
      bundle,
    } = useSelector(appSelector);
  const rawDefaultPageOptions = useSelector(pageOptionsSelector());
  const rollLabelPageOptions = useSelector(pageOptionsSelector(true, 'roll'));
  const dpleEnabled = useSelector(dpleEnabledSelector);
  const dispatch = useDispatch();

  const defaultPageOptions = useMemo(
    () => (
      dpleEnabled
        ? rawDefaultPageOptions.filter(({id}) => !DPLE_HIDDEN_OPTIONS.has(id))
        : rawDefaultPageOptions
    ),
    [rawDefaultPageOptions, dpleEnabled]
  );

  const [componentState, updateComponentState] = useImmer({
    expandedSelectBoxIndex: null,
    loadingIndex: null,
    application,
  });

  const setMultipleOverwrites = (overwrites) => {
    let overwrite = optionsOverwrite;
    overwrites.map(({parentId, id}) => {
      overwrite = {...overwrite, [parentId]: id};
    });

    dispatch(
      fetchChangeOptions({
        variables: {
          productId,
          defaultConfig,
          optionsOverwrite: overwrite,
          customQuantities,
          pageOptionsHide,
          pageOptionsFilter,
          priceListStyle,
          denyCustomFormats,
        },
      })
    );
  };

  const handleItemClick = ({parentId, id, is_selected}) => {
    if (!is_selected && !isFetching) {
      updateComponentState((draft) => {
        draft.loadingIndex = `${parentId}_${id}`;
      });

      dispatch(
        fetchChangeOptions({
          variables: {
            productId,
            client,
            customVat,
            defaultConfig,
            optionsOverwrite: {...optionsOverwrite, [parentId]: id},
            customQuantities,
            pageOptionsHide,
            pageOptionsFilter,
            priceListStyle,
            denyCustomFormats
          },
        })
      );

      if (parentId === 'paper') {
        dispatch(resetDpoData());
      }
    } else if (is_selected) {
      closeDropdown();
    }
  };

  const toggleDropdown = (i) => {
    if (!isFetching) {
      updateComponentState((draft) => {
        draft.expandedSelectBoxIndex =
          i === draft.expandedSelectBoxIndex ? null : i;
      });
    }
  };

  const openDropdown = (i) => {
    if (!isFetching) {
      updateComponentState((draft) => {
        draft.expandedSelectBoxIndex = i;
      });
    }
  };

  const closeDropdown = () => {
    updateComponentState((draft) => {
      draft.expandedSelectBoxIndex = null;
    });
  };

  const selectDefaultSelection = (ids, loadingIndex) => {
    if (!isFetching) {

      const newOptions = {};
      for (const id of ids) {
        const {default_value, selection_id} = pageOptions.filter(({id: poId}) => poId === id)[0];
        if (default_value !== selection_id) {
          newOptions[id] = default_value;
        }
      }

      if (Object.keys(newOptions).length > 0) {
        updateComponentState((draft) => {
          draft.loadingIndex = loadingIndex;
        });

        dispatch(
          fetchChangeOptions({
            variables: {
              productId,
              client,
              customVat,
              defaultConfig,
              optionsOverwrite: {...optionsOverwrite, ...newOptions},
              customQuantities,
              pageOptionsHide,
              pageOptionsFilter,
              priceListStyle,
              denyCustomFormats
            },
          })
        );
      }
    }
  };

  useEffect(() => {
    if (!isFetching) {
      updateComponentState((draft) => {
        draft.loadingIndex = null;
        draft.expandedSelectBoxIndex = null;
      });
    }
  }, [isFetching]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const appParam = urlParams.get('app');
    if (appParam !== null) {
      updateComponentState((draft) => {
        draft.application = appParam === 'ma' ? 1 : 0;
      });
    }
  }, []);

  return (
    <>
      {defaultPageOptions
        .map(
          (
            {
              id,
              label,
              selection_label,
              selection_sub_label,
              selection_id,
              selection_custom_size_type,
              values,
              tooltip
            },
            i
          ) => {
            switch (id) {
              case 'shape':
                return (
                  <TileListComponent
                    id='shape'
                    key={i}
                    parentId={id}
                    tileClassName="w-1/3 h-[5rem] tablet:w-1/4 laptop:w-1/5"
                    headline={
                      Drupal.t('Choose your @item:', {'@item': label})
                    }
                    items={values}
                    getItemDataAttributes={(item) => ({'data-shape': item.id})}
                    onItemClick={handleItemClick}
                    LoadingIcon={() => (
                      <ft-spinner size="small"></ft-spinner>
                    )}
                    loadingIndex={componentState.loadingIndex}
                    showIcon
                    isDisabled={isFetching}
                    isShape
                    reduceItems={values.length > 6}
                    reduceItemsText={Drupal.t("Show less shapes")}
                    expandItemsText={Drupal.t("Show more shapes")}
                    initialSelectId={values.find(item => item.is_selected)?.id}
                    dynamicItemClassNames={({i, selectedIndex, itemsReduced}) => (
                      [
                        // Hide all items which are above 5 (smallest list amount on mobile) except the selected one
                        i >= 6 && i !== selectedIndex && itemsReduced ? "hidden" : "",
                        // Remove one item for mobile but not for tablet
                        i === 3 && i !== selectedIndex && selectedIndex >= 6 && selectedIndex <= 7 && itemsReduced ? "hidden tablet:flex" : "",
                        // Remove one item for mobile but not for laptop
                        i === 4 && i !== selectedIndex && selectedIndex >= 8 && selectedIndex <= 9 && itemsReduced ? "hidden laptop:flex" : "",
                        // Remove another item for mobile only if selectedIndex is big enough
                        i === 5 && i !== selectedIndex && selectedIndex >= 10 && itemsReduced ? "hidden" : "",
                        // Restore tiles at breakpoints
                        i >= 6 && i <= 7 && i !== selectedIndex && itemsReduced ? "tablet:flex" : "",
                        i >= 8 && i <= 9 && i !== selectedIndex && itemsReduced ? "laptop:flex" : "",
                      ]
                    )}
                    Icon={({id, label = ''}) => <ShapeIcon {...{
                      id: overwrite_icons[id] ? overwrite_icons[id] : id,
                      attributes: {title: label}
                    }}
                    />
                    }
                    {...{instances}}
                  />
                );

              case 'format':
                return (
                  <Format
                    getItemDataAttributes={(item) => ({'data-format': item.id})}
                    key={i}
                    headline={
                      Drupal.t('Choose your @item:', {'@item': label})
                    }
                    toggleDropdown={() => toggleDropdown(i)}
                    openDropdown={() => openDropdown(i)}
                    isExpanded={componentState.expandedSelectBoxIndex === i}
                    parentId={id}
                    onItemClick={handleItemClick}
                    loadingIndex={componentState.loadingIndex}
                    {...{
                      isFetching,
                      closeDropdown,
                      values,
                      selection_label,
                      selection_id,
                      denyCustomFormats,
                      custom_format,
                      shapes: pageOptions
                        .filter(({id}) => id === 'shape')
                        .shift(),
                      papers: pageOptions
                        .filter(({id}) => id === 'paper')
                        .shift(),
                      setMultipleOverwrites,
                      lastChanges,
                      bundle,
                    }}
                  />
                );

              case 'paper':
                return (
                  <Paper
                    key={i}
                    headline={
                      Drupal.t('Choose your @item:', {'@item': label})
                    }
                    toggleDropdown={() => toggleDropdown(i)}
                    isExpanded={componentState.expandedSelectBoxIndex === i}
                    parentId={id}
                    onItemClick={handleItemClick}
                    loadingIndex={componentState.loadingIndex}
                    {...{
                      isFetching,
                      values,
                      selection_id,
                      selection_label,
                      selection_sub_label,
                      closeDropdown,
                      previewIconsPath,
                      lastChanges,
                      productId
                    }}
                  />
                );

              case 'book_binding':
                return (
                  <BookBinding
                    key={i}
                    headline={
                      Drupal.t('Choose your @item:', {'@item': label})
                    }
                    toggleDropdown={() => toggleDropdown(i)}
                    isExpanded={componentState.expandedSelectBoxIndex === i}
                    parentId={id}
                    onItemClick={handleItemClick}
                    loadingIndex={componentState.loadingIndex}
                    {...{
                      isFetching,
                      values,
                      selection_label,
                      closeDropdown,
                      previewIconsPath,
                    }}
                  />
                );

              case 'finishing':
                return (
                  <Finishing
                    key={i}
                    parentId={id}
                    headline={
                      Drupal.t('Choose your @item:', {'@item': label})
                    }
                    onItemClick={handleItemClick}
                    LoadingIcon={() => (
                      <ft-spinner size="small"></ft-spinner>
                    )}
                    loadingIndex={componentState.loadingIndex}
                    toggleDropdown={() => toggleDropdown(i)}
                    isExpanded={componentState.expandedSelectBoxIndex === i}
                    {...{
                      isFetching,
                      values,
                      selection_id,
                      selection_label,
                      selection_sub_label,
                      closeDropdown,
                      previewIconsPath,
                    }}
                  />
                );

              case 'data_check':
                return (
                  <DataCheck
                    key={i}
                    parentId={id}
                    headline={
                      Drupal.t('Choose your @item:', {'@item': label})
                    }
                    onItemClick={handleItemClick}
                    loadingIndex={componentState.loadingIndex}
                    {...{
                      isFetching,
                      values,
                      tooltip
                    }}
                  />
                );

              case 'pages':
                return (
                  <Page
                    key={i}
                    parentId={id}
                    headline={
                      Drupal.t('Choose your @item:', {'@item': label})
                    }
                    onItemClick={handleItemClick}
                    loadingIndex={componentState.loadingIndex}
                    {...{
                      isFetching,
                      values,
                      tooltip
                    }}
                  />
                );

              case 'model':
                return (
                  <TileListComponent
                    key={i}
                    id={id}
                    parentId={id}
                    getItemDataAttributes={(item) => ({[`data-${id}`]: item.id})}
                    tileClassName="w-1/3 h-[5rem]"
                    isDisabled={isFetching}
                    onItemClick={handleItemClick}
                    headline={
                      Drupal.t('Choose your @item:', {'@item': label})
                    }
                    items={values}
                    LoadingIcon={() => (
                      <ft-spinner size="small"></ft-spinner>
                    )}
                    loadingIndex={componentState.loadingIndex}
                    showIcon
                    showLabel
                    Icon={ModelIcon}
                  />
                );

              case 'delivery_format':
                return (
                  <TileListComponent
                    key={i}
                    id={id}
                    parentId={id}
                    getItemDataAttributes={(item) => ({[`data-${id}`]: item.id})}
                    tileClassName="w-1/2 h-6xl"
                    isDisabled={isFetching}
                    onItemClick={handleItemClick}
                    headline={
                      Drupal.t('Choose your @item:', {'@item': label})
                    }
                    items={values}
                    LoadingIcon={() => (
                      <ft-spinner size="small"></ft-spinner>
                    )}
                    loadingIndex={componentState.loadingIndex}
                  />
                );

              case 'refinement':
                return (
                  <Refinement
                    key={i}
                    parentId={id}
                    headline={
                      Drupal.t('Choose your @item:', {'@item': label})
                    }
                    onItemClick={handleItemClick}
                    loadingIndex={componentState.loadingIndex}
                    linkText={Drupal.t('Embossing')}
                    {...{
                      isFetching,
                      values,
                    }}
                  />
                );

              default:
                return (
                  <SelectBoxComponent
                    id={id}
                    key={i}
                    headline={
                      Drupal.t('Choose your @item:', {'@item': label})
                    }
                    showIcon={values.length > 1}
                    onClick={() => toggleDropdown(i)}
                    onClickOutside={closeDropdown}
                    isExpanded={componentState.expandedSelectBoxIndex === i}
                    disabledClassName="disabled"
                    isDisabled={isFetching}
                    DropdownContent={
                      <div className="absolute z-10 mt-5xs w-full rounded-sm bg-white shadow-lg">
                        <DropdownContent
                          parentId={id}
                          values={values}
                          onItemClick={handleItemClick}
                          loadingIndex={componentState.loadingIndex}
                        />
                      </div>
                    }
                  >
                    <span className="flex items-center justify-center">
                      <span className="ml-3 block truncate font-bold text-sm">
                        {selection_label}
                      </span>
                    </span>
                  </SelectBoxComponent>
                );
            }
          }
        )}
      <If condition={show_roll_label_options}>
        <RollLabelOptions
          i={defaultPageOptions.length}
          loadingIndex={componentState.loadingIndex}
          {...{isFetching, selectDefaultSelection, applicationPreSelection: componentState.application}}>
          {rollLabelPageOptions
            .map(
              (
                {
                  id,
                  label,
                  selection_id,
                  selection_label,
                  selection_sub_label,
                  selection_custom_size_type,
                  values,
                  tooltip,
                },
                i
              ) => {
                switch (id) {
                  case 'model':
                    return (
                      <RollDirection
                        key={i}
                        id='model'
                        headline={
                          Drupal.t('Choose your @item:', {'@item': label})
                        }
                        toggleDropdown={() => toggleDropdown(i)}
                        isExpanded={componentState.expandedSelectBoxIndex === i}
                        parentId={id}
                        onItemClick={handleItemClick}
                        loadingIndex={componentState.loadingIndex}
                        {...{
                          isFetching,
                          values,
                          selection_id,
                          selection_label,
                          selection_sub_label,
                          closeDropdown,
                          previewIconsPath,
                        }}
                      />
                    );

                  case 'refinement':
                    return (
                      <Refinement
                        key={i}
                        parentId={id}
                        headline={
                          Drupal.t('Choose your @item:', {'@item': label})
                        }
                        onItemClick={handleItemClick}
                        loadingIndex={componentState.loadingIndex}
                        linkText={Drupal.t('Roll core')}
                        tileClassName="w-full h-6xl laptop:w-1/3 laptop:h-[4.375rem]"
                        {...{
                          isFetching,
                          values,
                          tooltip
                        }}
                      />
                    );
                }
              }
            )}
        </RollLabelOptions>
      </If>
    </>
  );
};

const DropdownContent = ({parentId, values, onItemClick, loadingIndex}) => {
  return (
    <ul
      role="listbox"
      aria-labelledby={`listbox-${parentId}-label`}
      className={cn(
        "dropdown-content overflow-auto m-0 max-h-[50vh] laptop:max-h-[40vh]"
      )}
    >
      {values.map(({label, is_selected, id}, i) => (
        <li
          tabIndex="0"
          key={i}
          id={`listbox-item-${i}`}
          role="option"
          aria-selected={is_selected}
          className={cn(
            'relative flex flex-row items-center py-sm px-lg border-b border-b-neutral-medium cursor-pointer hover:bg-neutral-light',
            is_selected || loadingIndex === `${parentId}_${id}`
              ? 'bg-neutral-light ring-2 ring-inset ring-[color:var(--theme-link-border-color-4)] border-b-0'
              : 'bg-white'
          )}
          onClick={() => onItemClick({parentId, id, is_selected})}
        >
          <div className="flex items-center">
            <span className="font-semibold truncate">{label}</span>
          </div>
          <If condition={is_selected || loadingIndex === `${parentId}_${id}`}>
            <div className="absolute inset-y-0 right-0 pr-lg flex items-center">
              <Choose>
                <When condition={loadingIndex === `${parentId}_${id}`}>
                  <ft-spinner size="small"></ft-spinner>
                </When>
                <Otherwise>
                  <ft-icon name="check"></ft-icon>
                </Otherwise>
              </Choose>
            </div>
          </If>
        </li>
      ))}
    </ul>
  );
};

export default Parts;
